
import { max } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { Union, Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { union_type, tuple_type, record_type, list_type, int32_type, string_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { copiesOfArray, Component_$reflection } from "../Common/CommonTypes.js";
import { head, collect, tryHead, choose, length, append, reverse, empty, singleton, cons, fold, isEmpty, map, filter, sortBy } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { stringHash, comparePrimitives, numberHash, int32ToString, arrayHash, equalArrays, safeHash, equals, compare, compareArrays } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { join, concat, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { tryIntOfParamInt, paramNamesOfExpr, ParamExpression, evaluateParamExpression, ParamSlot, CompSlotName, tryFindSlot } from "../Common/ParameterTypes.js";
import { containsKey, tryFind, ofList, empty as empty_1, ofSeq } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { fromInt32 } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { List_distinctBy, List_groupBy, List_countBy, List_distinct } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { map as map_1, delay, toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";
import { defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { contains, ofList as ofList_1 } from "../fable_modules/fable-library-js.5.13.0/Set.js";

/**
 * How many bits the select input of an array multiplexer has: enough to index the copies, and
 * never zero, since a width of zero is not a width.
 * 
 * ceil(log2 copies), counted by shifting an int rather than through ParameterTypes.clog2, which
 * takes a bigint. A copy count is small by construction - ArrayElaborate.Constants.maxArrayCopies
 * bounds it, as a bus width is bounded - so converting one to a bigint to count its bits would be
 * work for nothing. The same answer as clog2 for every value this is asked about.
 */
export function arraySelectWidth(copies) {
    const bits = (v_mut, acc_mut) => {
        bits:
        while (true) {
            const v = v_mut, acc = acc_mut;
            if (v <= 1) {
                return acc | 0;
            }
            else {
                v_mut = (v >> 1);
                acc_mut = (acc + 1);
                continue bits;
            }
            break;
        }
    };
    return max(1, (copies <= 1) ? 0 : (bits(copies - 1, 0) + 1)) | 0;
}

/**
 * The name of the sheet port an unmatched JoinIn becomes. The direction is in the name as well as
 * in the port list so that the two ends of one channel read apart, and so that the generated names
 * sit further from anything a user would write.
 */
export function joinInPortName(label, num) {
    return `${label}_in_${num}`;
}

/**
 * The name of the sheet port an unmatched JoinOut becomes.
 */
export function joinOutPortName(label, num) {
    return `${label}_out_${num}`;
}

/**
 * The name of the select input a MuxOut adds to the sheet, beside the output of its own name.
 */
export function muxSelectPortName(label) {
    return label + "_sel";
}

/**
 * Something wrong with an array component's sheet: what to say, and what to point at.
 * 
 * The message says WHAT IS NOT ALLOWED and stops. Why it is not allowed belongs in the
 * documentation - a user reading an error is trying to fix a sheet, not to learn the design of the
 * feature, and a sentence of reasoning after the fault buries it.
 * 
 * Components are what the simulator highlights in red, so a problem names the ones a user has to
 * look at. Empty only where the fault is the sheet's own settings and no component is at fault.
 */
export class ArrayProblem extends Record {
    constructor(Message, Components) {
        super();
        this.Message = Message;
        this.Components = Components;
    }
}

export function ArrayProblem_$reflection() {
    return record_type("ArrayExpand.ArrayProblem", [], ArrayProblem, () => [["Message", string_type], ["Components", list_type(int32_type)]]);
}

/**
 * One end of a channel: a join component, in one copy, on the channel that copy puts it on.
 */
export class JoinEnd extends Record {
    constructor(Comp, Copy, Num) {
        super();
        this.Comp = Comp;
        this.Copy = (Copy | 0);
        this.Num = (Num | 0);
    }
}

export function JoinEnd_$reflection() {
    return record_type("ArrayExpand.JoinEnd", [], JoinEnd, () => [["Comp", Component_$reflection()], ["Copy", int32_type], ["Num", int32_type]]);
}

/**
 * What the joins of an array design sheet come to.
 */
export class JoinWiring extends Record {
    constructor(Matched, UnmatchedOut, UnmatchedIn, Ends, Problems) {
        super();
        this.Matched = Matched;
        this.UnmatchedOut = UnmatchedOut;
        this.UnmatchedIn = UnmatchedIn;
        this.Ends = Ends;
        this.Problems = Problems;
    }
}

export function JoinWiring_$reflection() {
    return record_type("ArrayExpand.JoinWiring", [], JoinWiring, () => [["Matched", list_type(tuple_type(JoinEnd_$reflection(), JoinEnd_$reflection()))], ["UnmatchedOut", list_type(JoinEnd_$reflection())], ["UnmatchedIn", list_type(JoinEnd_$reflection())], ["Ends", list_type(JoinEnd_$reflection())], ["Problems", list_type(ArrayProblem_$reflection())]]);
}

function joinComps(isOut, _arg1_, _arg1__1) {
    return sortBy((comp_1) => [comp_1.Y, comp_1.X], filter((comp) => {
        const matchValue = comp.Type;
        let matchResult;
        switch (matchValue.tag) {
            case 52: {
                if (isOut) {
                    matchResult = 0;
                }
                else {
                    matchResult = 1;
                }
                break;
            }
            case 53: {
                if (isOut) {
                    matchResult = 1;
                }
                else {
                    matchResult = 0;
                }
                break;
            }
            default:
                matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                return true;
            default:
                return false;
        }
    }, _arg1_), {
        Compare: (x, y) => (compareArrays(x, y) | 0),
    });
}

function joinWidthAndNum(comp) {
    const matchValue = comp.Type;
    let matchResult, n, w;
    switch (matchValue.tag) {
        case 52: {
            matchResult = 0;
            n = matchValue.fields[1];
            w = matchValue.fields[0];
            break;
        }
        case 53: {
            matchResult = 0;
            n = matchValue.fields[1];
            w = matchValue.fields[0];
            break;
        }
        default:
            matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return [w, n];
        default:
            return toFail(`joinWidthAndNum called on ${matchValue}, which is not a join`);
    }
}

function channelOf(info, slots, comp, copy) {
    const patternInput = joinWidthAndNum(comp);
    const matchValue = tryFindSlot(new ParamSlot(comp.Id, CompSlotName.JoinNum), slots);
    if (matchValue != null) {
        const exprSpec = matchValue;
        const matchValue_1 = evaluateParamExpression(ofSeq([[info.LoopParam, new ParamExpression(/* PInt */ 0, [fromInt32(copy)])]], {
            Compare: (x, y) => (compare(x, y) | 0),
        }), exprSpec.Expression);
        if (matchValue_1.tag === 1) {
            const outOfScope = map((_arg) => concat("\'", _arg.fields[0], "\'"), List_distinct(filter((name) => !equals(name, info.LoopParam), paramNamesOfExpr(exprSpec.Expression)), {
                Equals: equals,
                GetHashCode: (x_1) => (safeHash(x_1) | 0),
            }));
            if (isEmpty(outOfScope)) {
                return new FSharpResult$2(/* Error */ 1, [concat("cannot be worked out - ", matchValue_1.fields[0])]);
            }
            else {
                return new FSharpResult$2(/* Error */ 1, [`names ${join(", ", outOfScope)}; only the loop variable '${info.LoopParam.fields[0]}' may be used`]);
            }
        }
        else {
            const matchValue_2 = tryIntOfParamInt(matchValue_1.fields[0]);
            if (matchValue_2 == null) {
                return new FSharpResult$2(/* Error */ 1, ["is too large to be a channel number"]);
            }
            else {
                return new FSharpResult$2(/* Ok */ 0, [matchValue_2]);
            }
        }
    }
    else {
        return new FSharpResult$2(/* Ok */ 0, [patternInput[1]]);
    }
}

function endsOf(info, slots, canvas_, canvas__1, isOut) {
    const canvas = [canvas_, canvas__1];
    const side = isOut ? "Join out" : "Join in";
    const tupledArg_2 = fold((tupledArg, comp) => fold((tupledArg_1, copy) => {
        const ends_1 = tupledArg_1[0];
        const problems_1 = tupledArg_1[1];
        const matchValue = channelOf(info, slots, comp, copy);
        if (matchValue.tag === 1) {
            return [cons(new JoinEnd(comp, copy, joinWidthAndNum(comp)[1]), ends_1), cons(new ArrayProblem(`${side} '${comp.Label}': channel number ${matchValue.fields[0]}`, singleton(comp.Id)), problems_1)];
        }
        else {
            return [cons(new JoinEnd(comp, copy, matchValue.fields[0]), ends_1), problems_1];
        }
    }, [tupledArg[0], tupledArg[1]], toList(rangeDouble(0, 1, copiesOfArray(info) - 1))), [empty(), empty()], joinComps(isOut, canvas[0], canvas[1]));
    return [reverse(tupledArg_2[0]), reverse(tupledArg_2[1])];
}

function duplicateLabels(side, comps) {
    return map((tupledArg_1) => {
        const _arg_1 = tupledArg_1[0];
        const num = _arg_1[1] | 0;
        const label = _arg_1[0];
        return new ArrayProblem(`${tupledArg_1[1]} ${side} components are on channel ${num} of '${label}'`, map((c_1) => c_1.Id, filter((c) => {
            if (c.Label === label) {
                return joinWidthAndNum(c)[1] === num;
            }
            else {
                return false;
            }
        }, comps)));
    }, filter((tupledArg) => (tupledArg[1] > 1), List_countBy((comp) => [comp.Label, joinWidthAndNum(comp)[1]], comps, {
        Equals: equalArrays,
        GetHashCode: (x) => (arrayHash(x) | 0),
    })));
}

/**
 * Which JoinOut in which copy drives which JoinIn in which copy, and which ends are left over.
 * 
 * The one place join semantics live. parseDiagramSignature asks it what ports the sheet has, and
 * the expansion asks it what wires to draw between the copies, so the two cannot disagree about
 * which end of a chain is loose.
 * 
 * A JoinOut end and a JoinIn end MATCH when they carry the same label and the same channel number;
 * a match is a wire from one copy to another. Everything left over becomes a port on the sheet's
 * outline - an unmatched JoinOut an output, an unmatched JoinIn an input - which is what makes the
 * ends of a chain the array's own connections without anything having to say which copies they are.
 */
export function joinsOf(info, paramDefs, canvas_, canvas__1) {
    let option_1;
    const canvas = [canvas_, canvas__1];
    const slots = defaultArg((option_1 = paramDefs, (option_1 != null) ? option_1.ParamSlots : undefined), empty_1({
        Compare: (x, y) => (compare(x, y) | 0),
    }));
    const patternInput = endsOf(info, slots, canvas[0], canvas[1], true);
    const outEnds = patternInput[0];
    const patternInput_1 = endsOf(info, slots, canvas[0], canvas[1], false);
    const inEnds = patternInput_1[0];
    const negatives = map((e_1) => (new ArrayProblem(`'${e_1.Comp.Label}' is on negative channel ${e_1.Num} in copy ${e_1.Copy}`, singleton(e_1.Comp.Id))), filter((e) => (e.Num < 0), append(outEnds, inEnds)));
    const nameProblems = append(duplicateLabels("Join out", joinComps(true, canvas[0], canvas[1])), duplicateLabels("Join in", joinComps(false, canvas[0], canvas[1])));
    const grouped = List_groupBy((e_2) => [e_2.Comp.Label, e_2.Num], outEnds, {
        Equals: equalArrays,
        GetHashCode: (x_1) => (arrayHash(x_1) | 0),
    });
    const outClashes = map((tupledArg_1) => {
        const _arg_1 = tupledArg_1[0];
        const es_1 = tupledArg_1[1];
        return new ArrayProblem(`Join out '${_arg_1[0]}' drives channel ${_arg_1[1]} in copies ${join(", ", map((e_4) => int32ToString(e_4.Copy), es_1))}`, List_distinct(map((e_5) => e_5.Comp.Id, es_1), {
            Equals: equals,
            GetHashCode: (x_3) => (safeHash(x_3) | 0),
        }));
    }, filter((tupledArg) => {
        const es = tupledArg[1];
        if (length(es) > 1) {
            return length(List_distinctBy((e_3) => e_3.Comp.Id, es, {
                Equals: equals,
                GetHashCode: (x_2) => (safeHash(x_2) | 0),
            })) === 1;
        }
        else {
            return false;
        }
    }, grouped));
    const outByChannel = ofList(choose((tupledArg_2) => {
        const option_4 = tryHead(tupledArg_2[1]);
        if (option_4 != null) {
            return [tupledArg_2[0], option_4];
        }
        else {
            return undefined;
        }
    }, grouped), {
        Compare: (x_4, y_4) => (compareArrays(x_4, y_4) | 0),
    });
    const matched = sortBy((tupledArg_3) => {
        const o = tupledArg_3[0];
        return [o.Comp.Y, o.Comp.X, o.Copy, tupledArg_3[1].Copy];
    }, choose((inEnd) => {
        const option_6 = tryFind([inEnd.Comp.Label, inEnd.Num], outByChannel);
        if (option_6 != null) {
            return [option_6, inEnd];
        }
        else {
            return undefined;
        }
    }, inEnds), {
        Compare: (x_5, y_5) => (compareArrays(x_5, y_5) | 0),
    });
    const channelsRead = ofList_1(map((e_7) => [e_7.Comp.Label, e_7.Num], inEnds), {
        Compare: (x_6, y_6) => (compareArrays(x_6, y_6) | 0),
    });
    const Ends = append(outEnds, inEnds);
    return new JoinWiring(matched, filter((e_8) => !contains([e_8.Comp.Label, e_8.Num], channelsRead), outEnds), filter((e_9) => !containsKey([e_9.Comp.Label, e_9.Num], outByChannel), inEnds), Ends, append(nameProblems, append(patternInput[1], append(patternInput_1[1], append(negatives, outClashes)))));
}

/**
 * The unmatched ends of one join component, grouped by the channel they are on and in channel
 * order, so that a sheet with several loose ends lists them predictably.
 * 
 * One PORT per channel, and EVERY end on that channel with it. A port is named after the channel,
 * so a channel several copies are loose on can only be one port - but it is a port of all of them,
 * and each has to be wired to it. A number that does not vary with the loop variable is the
 * extreme case (every copy on one channel); `i/2` is the ordinary one, giving the array one input
 * per pair of copies. Keeping one end per channel and dropping the rest wired the first copy and
 * left the others' body ports dangling, which came out as an unconnected port on a sheet the user
 * cannot open.
 */
export function looseEndsOf(ends, comp) {
    return sortBy((tuple) => (tuple[0] | 0), List_groupBy((e_1) => (e_1.Num | 0), filter((e) => equals(e.Comp.Id, comp.Id), ends), {
        Equals: (x, y) => (x === y),
        GetHashCode: (x) => (numberHash(x) | 0),
    }), {
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    });
}

/**
 * What the array does with one of its own ports - which is also how the wrapper must wire it.
 * 
 * The reason this is a type rather than two functions: the sheet's SIGNATURE and the wrapper that
 * realises it are the same seven rules read two ways, and stating them twice is how a sheet's
 * declared ports and its wrapper's actual ports come to disagree. They are stated once, here, and
 * arrayOutlineOf takes the names and widths off them while ArrayElaborate builds the components.
 */
export class PortRole extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["ToEveryCopy", "ToCopies", "SelectOf", "FromCopy", "Concatenated", "Multiplexed"];
    }
    static ToEveryCopy = new PortRole(0, []);
    static Concatenated = new PortRole(4, []);
    static Multiplexed = new PortRole(5, []);
}

export function PortRole_$reflection() {
    return union_type("ArrayExpand.PortRole", [], PortRole, () => [[], [["Item", list_type(int32_type)]], [["Item", Component_$reflection()]], [["Item", int32_type]], [], []]);
}

/**
 * One port of an array component's outline: what it is called, how wide it is, which drawn
 * component it comes from, and what the array does with it.
 */
export class OutlinePort extends Record {
    constructor(Name, Width, Comp, Role) {
        super();
        this.Name = Name;
        this.Width = (Width | 0);
        this.Comp = Comp;
        this.Role = Role;
    }
}

export function OutlinePort_$reflection() {
    return record_type("ArrayExpand.OutlinePort", [], OutlinePort, () => [["Name", string_type], ["Width", int32_type], ["Comp", Component_$reflection()], ["Role", PortRole_$reflection()]]);
}

/**
 * The ports an array design sheet has, and what is wrong with the sheet if anything.
 * 
 * DERIVED from the sheet's contents and its copy count, rather than being its Input1 and Output
 * components - which is the whole of what makes an array sheet different from every other sheet
 * as far as anything outside this module is concerned. The copy count is a plain integer on the
 * sheet, so this is a fact about the SHEET, exactly as an ordinary sheet's ports are.
 * 
 * Ports come in the (Y, X) order of the components that generate them - the order
 * getOrderedCompLabels already uses. A MuxOut contributes BOTH an input and an output, so the two
 * lists are over the same components in the same order and a mux select lands in the middle of the
 * inputs, which is exactly where the outline puts it.
 * 
 * The canvas passed in must already be resolved at whatever bindings are wanted, as
 * signatureOfInstance resolves it: the widths here are read off the components.
 */
export function outlinePortsOf(info, paramDefs, canvas_, canvas__1) {
    const canvas = [canvas_, canvas__1];
    const copies = copiesOfArray(info) | 0;
    const wiring = joinsOf(info, paramDefs, canvas[0], canvas[1]);
    const ordered = sortBy((comp) => [comp.Y, comp.X], canvas[0], {
        Compare: (x, y) => (compareArrays(x, y) | 0),
    });
    const port = (name, width, comp_1, role) => (new OutlinePort(name, width, comp_1, role));
    return [[collect((comp_2) => {
        const matchValue = comp_2.Type;
        switch (matchValue.tag) {
            case 0:
                return singleton(port(comp_2.Label, matchValue.fields[0], comp_2, PortRole.ToEveryCopy));
            case 53:
                return map((tupledArg) => port(joinInPortName(comp_2.Label, tupledArg[0]), matchValue.fields[0], comp_2, new PortRole(/* ToCopies */ 1, [map((e) => (e.Copy | 0), tupledArg[1])])), looseEndsOf(wiring.UnmatchedIn, comp_2));
            case 51:
                return singleton(port(muxSelectPortName(comp_2.Label), arraySelectWidth(copies), comp_2, new PortRole(/* SelectOf */ 2, [comp_2])));
            default:
                return empty();
        }
    }, ordered), collect((comp_3) => {
        const matchValue_1 = comp_3.Type;
        switch (matchValue_1.tag) {
            case 1:
                return toList(delay(() => map_1((i) => port(`${comp_3.Label}_${i}`, matchValue_1.fields[0], comp_3, new PortRole(/* FromCopy */ 3, [i])), rangeDouble(0, 1, copies - 1))));
            case 50:
                return singleton(port(comp_3.Label, matchValue_1.fields[0] * copies, comp_3, PortRole.Concatenated));
            case 52:
                return map((tupledArg_1) => port(joinOutPortName(comp_3.Label, tupledArg_1[0]), matchValue_1.fields[0], comp_3, new PortRole(/* FromCopy */ 3, [head(tupledArg_1[1]).Copy])), looseEndsOf(wiring.UnmatchedOut, comp_3));
            case 51:
                return singleton(port(comp_3.Label, matchValue_1.fields[0], comp_3, PortRole.Multiplexed));
            default:
                return empty();
        }
    }, ordered)], wiring];
}

/**
 * The array component's ports as names and widths - what every sheet signature is - and what is
 * wrong with the sheet if anything.
 */
export function arrayOutlineOf(info, paramDefs, canvas_, canvas__1) {
    const canvas = [canvas_, canvas__1];
    const patternInput = outlinePortsOf(info, paramDefs, canvas[0], canvas[1]);
    const outPorts = patternInput[0][1];
    const inPorts = patternInput[0][0];
    const named = (ports) => map((p) => [p.Name, p.Width], ports);
    const matchValue = named(inPorts);
    const outputs = named(outPorts);
    const inputs = matchValue;
    const collisions = (side, ports_1) => map((tupledArg_1) => {
        const name = tupledArg_1[0];
        return new ArrayProblem(`This sheet derives two ${side}s called '${name}'`, List_distinct(map((p_3) => p_3.Comp.Id, filter((p_2) => (p_2.Name === name), ports_1)), {
            Equals: equals,
            GetHashCode: (x_1) => (safeHash(x_1) | 0),
        }));
    }, filter((tupledArg) => (tupledArg[1] > 1), List_countBy((p_1) => p_1.Name, ports_1, {
        Equals: (x, y) => (x === y),
        GetHashCode: (x) => (stringHash(x) | 0),
    })));
    const matchValue_2 = patternInput[1].Problems;
    if (isEmpty(matchValue_2)) {
        return [[inputs, outputs], append(collisions("input", inPorts), collisions("output", outPorts))];
    }
    else {
        return [[inputs, outputs], matchValue_2];
    }
}

